/** "Brief me": asks Fusion AI for a morning brief, a nudge list or an end-of-day wrap right now and opens the result. */
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Loader2, Sun } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { AK, call, errText } from "./api";

const KINDS = [
  { kind: "morning", label: "Morning brief" },
  { kind: "nudges", label: "What needs a nudge" },
  { kind: "wrap", label: "End-of-day wrap" },
] as const;

export function BriefMenu({ onOpen, iconOnly, align = "right" }: { onOpen: (conversationId: string) => void; iconOnly?: boolean; align?: "left" | "right" }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey, true); };
  }, [open]);
  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(""), 4000);
    return () => clearTimeout(t);
  }, [note]);

  const ask = async (kind: (typeof KINDS)[number]["kind"]) => {
    setOpen(false); setBusy(true); setNote("");
    try {
      const r = await call<{ sent: boolean; conversationId?: string; note?: string }>("POST", "/api/assistant/brief", { kind });
      if (!r.ok || !r.body) setNote(errText(r.body, r.status));
      else if (r.body.sent && r.body.conversationId) {
        const cid = r.body.conversationId;
        await Promise.all([qc.invalidateQueries({ queryKey: AK.state }), qc.invalidateQueries({ queryKey: AK.thread(cid) })]);
        onOpen(cid);
      } else setNote(r.body.note || "Nothing to report right now.");
    } catch (e) { setNote((e as Error).message || "Could not reach ONE"); }
    setBusy(false);
  };

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        className={clsx("btn btn-sm", iconOnly && "btn-ghost btn-icon !h-8 !min-h-0 !w-8")}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Brief me"
        title="Brief me"
        disabled={busy}
        onClick={() => setOpen((o) => !o)}
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sun size={iconOnly ? 16 : 14} />}
        {!iconOnly && <span>Brief me</span>}
      </button>
      {open && (
        <div role="menu" className={clsx("absolute top-full z-50 mt-1 w-52", align === "left" ? "left-0" : "right-0", " rounded-lg border border-[var(--line)] bg-[var(--panel)] p-1 shadow-[var(--shadow-pop)]")}>
          {KINDS.map((k) => (
            <button key={k.kind} type="button" role="menuitem" className="block w-full rounded-md px-2.5 py-1.5 text-left text-sm hover:bg-[var(--panel-2)]" onClick={() => void ask(k.kind)}>
              {k.label}
            </button>
          ))}
        </div>
      )}
      {note && (
        <div role="status" className={clsx("absolute top-full z-50 mt-1 w-56", align === "left" ? "left-0" : "right-0", " rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2.5 py-2 text-xs text-[var(--text-2)] shadow-[var(--shadow-pop)]")}>
          {note}
        </div>
      )}
    </div>
  );
}
